import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, throwError, merge } from 'rxjs';
import { tap, catchError, shareReplay, finalize, take, startWith } from 'rxjs/operators';
import { CacheService } from './cache.service';
import {
  HTTP_CACHE_CONFIG,
  BYPASS_CACHE,
  CacheStrategy,
  CacheConfig,
} from './cache.types';

@Injectable()
export class HttpCacheInterceptor implements HttpInterceptor {
  private inFlightRequests = new Map<string, Observable<HttpEvent<any>>>();

  constructor(private cacheService: CacheService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    const bypassCache = req.context.get(BYPASS_CACHE);
    if (bypassCache) {
      return next.handle(req);
    }

    const cacheConfig = req.context.get(HTTP_CACHE_CONFIG);
    if (cacheConfig === null || cacheConfig === undefined) {
      return next.handle(req);
    }

    if (!cacheConfig.ttl || cacheConfig.ttl <= 0) {
      return next.handle(req);
    }

    const cacheKey = this.generateCacheKey(req, cacheConfig);
    return this.handleCacheStrategy(req, next, cacheKey, cacheConfig);
  }

  private handleCacheStrategy(
    req: HttpRequest<any>,
    next: HttpHandler,
    cacheKey: string,
    config: CacheConfig
  ): Observable<HttpEvent<any>> {
    const strategy = config.strategy || CacheStrategy.CACHE_FIRST;

    switch (strategy) {
      case CacheStrategy.CACHE_FIRST:
        return this.cacheFirstStrategy(req, next, cacheKey, config);
      case CacheStrategy.NETWORK_FIRST:
        return this.networkFirstStrategy(req, next, cacheKey, config);
      case CacheStrategy.CACHE_AND_NETWORK:
        return this.cacheAndNetworkStrategy(req, next, cacheKey, config);
      case CacheStrategy.NETWORK_ONLY:
        return this.networkOnlyStrategy(req, next, cacheKey, config);
      case CacheStrategy.STALE_WHILE_REVALIDATE:
        return this.staleWhileRevalidateStrategy(req, next, cacheKey, config);
      default:
        return this.cacheFirstStrategy(req, next, cacheKey, config);
    }
  }

  private cacheFirstStrategy(
    req: HttpRequest<any>,
    next: HttpHandler,
    cacheKey: string,
    config: CacheConfig
  ): Observable<HttpEvent<any>> {
    const cachedResponse = this.getCachedResponse(cacheKey);
    if (cachedResponse) {
      return of(cachedResponse);
    }

    const inFlight = this.getInFlightRequest(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    return this.makeRequestAndCache(req, next, cacheKey, config);
  }

  private networkFirstStrategy(
    req: HttpRequest<any>,
    next: HttpHandler,
    cacheKey: string,
    config: CacheConfig
  ): Observable<HttpEvent<any>> {
    return this.makeRequestAndCache(req, next, cacheKey, config).pipe(
      catchError((error) => {
        const cachedResponse = this.getCachedResponse(cacheKey);
        if (cachedResponse) {
          console.warn(`[HttpCache] Network failed, using stale cache for ${cacheKey}`);
          return of(cachedResponse);
        }
        return throwError(() => error);
      })
    );
  }

  private cacheAndNetworkStrategy(
    req: HttpRequest<any>,
    next: HttpHandler,
    cacheKey: string,
    config: CacheConfig
  ): Observable<HttpEvent<any>> {
    const cachedResponse = this.getCachedResponse(cacheKey);

    if (cachedResponse) {
      // Verificar si ya hay un background update en progreso
      const inFlight = this.getInFlightRequest(cacheKey);
      if (!inFlight) {
        // Solo disparar background update si NO hay uno en progreso
        this.makeRequestAndCache(req, next, cacheKey, config)
          .pipe(take(1))
          .subscribe({
            error: (err) => console.warn(`[HttpCache] Background update failed for ${cacheKey}`, err),
          });
      }

      return of(cachedResponse);
    }

    const inFlight = this.getInFlightRequest(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    return this.makeRequestAndCache(req, next, cacheKey, config);
  }

  private networkOnlyStrategy(
    req: HttpRequest<any>,
    next: HttpHandler,
    cacheKey: string,
    config: CacheConfig
  ): Observable<HttpEvent<any>> {
    this.cacheService.delete(cacheKey);
    return this.makeRequestAndCache(req, next, cacheKey, config);
  }

  private staleWhileRevalidateStrategy(
    req: HttpRequest<any>,
    next: HttpHandler,
    cacheKey: string,
    config: CacheConfig
  ): Observable<HttpEvent<any>> {
    const cachedResponse = this.getCachedResponse(cacheKey);

    if (cachedResponse) {
      // Verificar si ya hay un request en progreso
      const inFlight = this.getInFlightRequest(cacheKey);

      if (inFlight) {
        // Hay un request en progreso: emitir cache primero + unirse al request existente
        return merge(
          of(cachedResponse),
          inFlight
        );
      }

      // No hay request en progreso: emitir cache primero + hacer nuevo request
      // El componente recibirá DOS valores:
      // 1. Cache inmediato (stale/optimista)
      // 2. Respuesta fresca del servidor cuando termine
      return this.makeRequestAndCache(req, next, cacheKey, config).pipe(
        startWith(cachedResponse)
      );
    }

    // No hay cache: comportamiento normal (solo red)
    const inFlight = this.getInFlightRequest(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    return this.makeRequestAndCache(req, next, cacheKey, config);
  }

  private makeRequestAndCache(
    req: HttpRequest<any>,
    next: HttpHandler,
    cacheKey: string,
    config: CacheConfig
  ): Observable<HttpEvent<any>> {
    const request$ = next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
          this.cacheService.set(cacheKey, event.body, config);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inFlightRequests.delete(cacheKey))
    );

    this.inFlightRequests.set(cacheKey, request$);
    return request$;
  }

  private generateCacheKey(req: HttpRequest<any>, config: CacheConfig): string {
    return config.key || req.urlWithParams;
  }

  private getCachedResponse<T>(cacheKey: string): HttpResponse<T> | null {
    const cached = this.cacheService.get<T>(cacheKey);
    if (cached !== null) {
      return new HttpResponse({ body: cached, status: 200 });
    }
    return null;
  }

  private getInFlightRequest(cacheKey: string): Observable<HttpEvent<any>> | null {
    return this.inFlightRequests.get(cacheKey) || null;
  }
}
